import _ from "lodash";
import React from "react";
import classes from "./MultiSelectWithImage.scss";
import PropTypes from "prop-types";
import EmptyField from "UIComponents/EmptyField";
import { Tick } from "SvgComponents";
import update from "immutability-helper";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import classNames from "classnames";
import { FormInputError } from "@toddle-edu/ds-web";
import { IllustrationMap } from "./Utils";

const OptionItem = ({
  item,
  value,
  isSelected,
  onClickItem,
  error,
  tickDimension,
  fieldTypeConfig,
  selectedTickStyle,
  activeTickColor,
  tickContainerStyle,
  disabled,
}) => {
  const tickPosition = _.get(fieldTypeConfig, "tickPosition", "top");

  const optionItemContainerClass = classNames(
    { [classes.optionItemContainer]: true },
    { [classes.optionItemContainerActive]: isSelected },
    { [classes.error]: !!error }
  );

  const tickContainerClass = classNames(
    { [classes.tickContainer]: true },
    { [classes.tickTop]: tickPosition === "top" },
    { [classes.tickCenter]: tickPosition === "center" }
  );

  const labelClass = classNames({
    [classes.label]: true,
    ["text-label-3"]: true,
  });

  const subtextClass = classNames({
    [classes.subtext]: true,
    ["text-body-3"]: true,
  });

  const params = {
    value: value,
    operation: isSelected ? "remove" : "add",
  };

  return (
    <div
      className={optionItemContainerClass}
      onClick={e => {
        e.stopPropagation();
        !disabled ? onClickItem(params) : null;
      }}
    >
      {IllustrationMap[item.image]}
      <div className={tickContainerClass} style={tickContainerStyle}>
        {isSelected ? (
          <div style={selectedTickStyle}>
            {tickDimension ? (
              <Tick
                width={tickDimension}
                height={tickDimension}
                fillBackground={activeTickColor && activeTickColor}
              />
            ) : (
              <Tick fillBackground={activeTickColor && activeTickColor} />
            )}
          </div>
        ) : null}
      </div>
      <div className={classes.textContainer}>
        <div className={labelClass}>{item.label}</div>
        {item.subText ? (
          <div className={subtextClass}>{item.subText}</div>
        ) : null}
      </div>
    </div>
  );
};

class MultiSelectWithImage extends UIBaseComponent {
  errorId = _.uniqueId("form-error-");
  isValidValue = value => {
    return !!value && value.length > 0;
  };

  onClickItem = params => {
    const {
      value,
      multi,
      options,
      minimumSelectedOptionsCount,
      onMinimumSelectedOptionsRemove,
    } = this.props;
    let selectedOptions = value ?? [];

    if (multi) {
      switch (params["operation"]) {
        case "add":
          selectedOptions = update(selectedOptions, {
            $push: [params.value],
          });
          break;
        case "remove": {
          if (minimumSelectedOptionsCount) {
            if (_.size(selectedOptions) == minimumSelectedOptionsCount) {
              onMinimumSelectedOptionsRemove &&
                onMinimumSelectedOptionsRemove();
              break;
            }
          }
          const index = _.indexOf(value, params["value"]);
          selectedOptions = update(selectedOptions, {
            $splice: [[index, 1]],
          });
          break;
        }
      }
    } else {
      switch (params["operation"]) {
        case "add":
          selectedOptions = [params.value];
          break;
        case "remove":
          selectedOptions = [];
          break;
      }
    }

    this.updateValue(selectedOptions, {
      nodes: options,
    });
  };

  UNSAFE_componentWillReceiveProps = nextProps => {
    const { options } = this.props;

    if (_.xorBy(options, nextProps.options, "value").length > 0) {
      const updatedValue = _.filter(
        nextProps.value,
        val => _.findIndex(nextProps.options, { value: val }) != -1
      );

      this.updateValue(updatedValue, { nodes: options });
    }
  };

  updateNodesRef = ({ ref, id }) => {
    const { updateNodesRef } = this.props;
    if (updateNodesRef) {
      updateNodesRef({ ref, id });
    }
  };

  getOptionItemList = ({ options }) => {
    const {
      value,
      tickDimension,
      fieldTypeConfig,
      selectedTickStyle,
      activeTickColor,
      tickContainerStyle,
      textItemStyle,
      disabled,
    } = this.props;

    const { error } = this.state;

    if (!_.isEmpty(options)) {
      return _.map(options, itemObject => {
        return (
          <OptionItem
            disabled={disabled}
            item={itemObject}
            key={itemObject.value}
            value={itemObject.value}
            selectedValue={value}
            isSelected={_.includes(value, itemObject.value)}
            onClickItem={this.onClickItem}
            tickDimension={tickDimension}
            error={error}
            fieldTypeConfig={fieldTypeConfig}
            selectedTickStyle={selectedTickStyle}
            activeTickColor={activeTickColor}
            tickContainerStyle={tickContainerStyle}
            textItemStyle={textItemStyle}
          />
        );
      });
    } else {
      return <EmptyField title={this.props.emptyText} />;
    }
  };

  renderView = () => {
    const {
      value,
      options,
      customTextStyle = {},
      customLabelStyle = {},
    } = this.props;

    return (
      <div className={classes.viewContainer}>
        {_.map(value, item => {
          const selectedOption = _.find(options, { value: item });

          return (
            <div className={classes.selectedOptionContainer} key={item}>
              <div className="text-label-2" style={customLabelStyle}>
                {selectedOption.label}
              </div>
              <div
                className={classes.selectedOptionSubtext}
                style={customTextStyle}
              >
                {selectedOption.subText}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderEdit = () => {
    const { containerStyle, options } = this.props;
    const { error } = this.state;

    return (
      <div className={classes.container}>
        <div
          className={classes.optionContainer}
          style={containerStyle}
          role="group"
          aria-describedby={this.errorId}
        >
          {this.getOptionItemList({ options })}
        </div>
        <FormInputError id={this.errorId} error={error} />
      </div>
    );
  };
}

MultiSelectWithImage.propTypes = {
  ...UIBaseComponent.propTypes,
  options: PropTypes.array,
  multi: PropTypes.bool,
  selectedTickStyle: PropTypes.object,
  containerStyle: PropTypes.object,
};

MultiSelectWithImage.defaultProps = {
  ...UIBaseComponent.defaultProps,
  options: [],
  multi: true,
  selectedTickStyle: {},
  containerStyle: {},
};

export default MultiSelectWithImage;
